// @flow
/* eslint-disable no-use-before-define */
import * as React from 'react';
import { useIntl } from 'react-intl';

export const FieldNames = {
  URL: 'URL_COMPLETA',
  Domain: 'Dominio',
  Language: 'Idioma_language',
  BillingCountry: 'Billing_country',
  CountryEnum: 'Country_Picklist',

  GCLID: 'GCLID',
  ZC_GAD: 'zc_gad',
  UTM: 'UTM',
  AttributionSource: 'Fuente',
  AttributionMedium: 'Medio',
  AttributionChannel: 'Canal',
  AttributionCampaign: 'Campa_a',
};

export default function useGetLeadContextValues() {
  const language = useIntl().locale;

  React.useEffect(() => {
    // Preload analytics fields into session storage
    getAnalyticsFields();
  }, []);

  return React.useCallback(
    async (values?: { [string]: mixed }) => {
      const [analyticsFields, locationFields] = await Promise.all([
        getAnalyticsFields(values),
        fetchLocationFields(),
      ]);
      return {
        ...analyticsFields,
        ...locationFields,
        [FieldNames.URL]: window.location.href,
        [FieldNames.Domain]: window.location.hostname,
        [FieldNames.Language]: mapLanguage(language),
      };
    },
    [language],
  );
}

function mapLanguage(lang: string): string {
  switch (lang.toLowerCase()) {
    case 'en':
    case 'en-us':
    case 'en-gb':
      return 'English';
    case 'it':
    case 'it-it':
      return 'Italiano';
    case 'es':
    case 'es-es':
    // fall through
    default:
      return 'Español';
  }
}

const UTMS_FIRST_TOUCH_SESSION_STORAGE_KEY = 'utms_first_touch_v1';

function getAnalyticsFields<Values: { [string]: mixed }>(values?: Values) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {};
  }
  const url = new URL(window.location.href);
  const hasGclid =
    // Check if GCLID is passed in the values object, as there's a mechanism already
    // built in the Zoho Forms component to read it from session storage and pass it down
    (values &&
      typeof values === 'object' &&
      ((typeof values[FieldNames.GCLID.toLowerCase()] === 'string' &&
        // $FlowFixMe --> Reason: not inferring lowercased key properly
        values[FieldNames.GCLID.toLowerCase()].length > 0) ||
        (typeof values[FieldNames.GCLID] === 'string' &&
          values[FieldNames.GCLID].length > 0))) ||
    // Fallback to checking URL params
    url.searchParams.get('gclid');
  const hasFbclid = !!url.searchParams.get('fbclid');
  const hasMsclkid = !!url.searchParams.get('msclkid');

  const existingUtms = sessionStorage.getItem(
    UTMS_FIRST_TOUCH_SESSION_STORAGE_KEY,
  );
  const searchParams = existingUtms
    ? new URLSearchParams(existingUtms)
    : url.searchParams;

  const referrer = document.referrer ? new URL(document.referrer) : null;
  const utm = {
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_channel: searchParams.get('utm_channel'),
    utm_campaign: searchParams.get('utm_campaign'),
  };
  if (utm.utm_source || utm.utm_medium || utm.utm_channel || utm.utm_campaign) {
    const utmSearchParams = new URLSearchParams();
    Object.entries(utm).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 0) {
        utmSearchParams.set(key, value);
      }
    });
    sessionStorage.setItem(
      UTMS_FIRST_TOUCH_SESSION_STORAGE_KEY,
      utmSearchParams.toString(),
    );
    return {
      [FieldNames.UTM]: utmSearchParams.toString(),
      [FieldNames.AttributionSource]: utm.utm_source,
      [FieldNames.AttributionMedium]: utm.utm_medium,
      [FieldNames.AttributionChannel]: getAttributionChannel(),
      [FieldNames.AttributionCampaign]: utm.utm_campaign,
    };
  }
  if (referrer) {
    return {
      [FieldNames.AttributionSource]: referrer.hostname,
      [FieldNames.AttributionMedium]: 'Referral',
      [FieldNames.AttributionChannel]: getAttributionChannel(),
      [FieldNames.AttributionCampaign]: 'Referral',
    };
  }
  return {
    [FieldNames.AttributionSource]: 'Direct',
    [FieldNames.AttributionMedium]: 'None',
    [FieldNames.AttributionChannel]: getAttributionChannel(),
    [FieldNames.AttributionCampaign]: 'Direct',
  };

  function getAttributionChannel() {
    if (
      hasGclid ||
      hasMsclkid ||
      (utm.utm_medium &&
        ['cpc', 'ppc', 'paidsearch'].includes(utm.utm_medium.toLowerCase()))
    ) {
      return 'Paid Search';
    }

    // 2. Paid Social
    if (
      hasFbclid ||
      (utm.utm_medium &&
        ['paidsocial', 'paid_social'].includes(utm.utm_medium.toLowerCase()))
    ) {
      return 'Paid Social';
    }

    // 3. Display
    if (
      utm.utm_medium &&
      ['display', 'banner', 'cpm', 'programmatic'].includes(
        utm.utm_medium.toLowerCase(),
      )
    ) {
      return 'Display';
    }

    // 4. Email
    if (
      utm.utm_medium &&
      ['email', 'newsletter', 'edm'].includes(utm.utm_medium.toLowerCase())
    ) {
      return 'Email';
    }

    // 5. Affiliates
    if (
      utm.utm_medium &&
      ['affiliate', 'affiliates'].includes(utm.utm_medium.toLowerCase())
    ) {
      return 'Affiliates';
    }

    // 6. Organic Social
    if (
      utm.utm_medium === 'social' ||
      (referrer &&
        getSocialNetworks().some(sn => referrer.toString().indexOf(sn) > -1))
    ) {
      return 'Organic Social';
    }

    // 7. Organic Search
    if (
      referrer &&
      getSearchEngines().some(se => referrer.toString().indexOf(se) > -1)
    ) {
      return 'Organic Search';
    }

    // 8. Referral
    if (referrer) {
      return 'Referral';
    }

    // 9. Direct (fallback)
    return 'Direct';
  }
}

async function fetchLocationFields() {
  try {
    const response = await fetch('https://ip2c.org/self');
    const text = await response.text();
    const [status, code2, unusedCode3, name] = text.split(';');
    if (status !== '1') {
      console.error(
        `Failed to fetch location data for current IP. Status: ${response.status} ${response.statusText}. Payload: ${text}`,
      );
      return undefined;
    }

    return {
      [FieldNames.BillingCountry]: name || '',
      [FieldNames.CountryEnum]: (() => {
        switch (code2) {
          case 'ES':
            return 'España';
          case 'IT':
            return 'Italy';
          case 'MX':
            return 'Mexico';
          case 'FR':
            return 'France';
          case 'US':
            return 'USA';
          case 'PT':
            return 'Portugal';
          default:
            break;
        }
        switch (getRegionByCountryCode(code2)) {
          case 'EU':
            return 'Otros - Europa';
          case 'LATAM':
            return 'LATAM';
          default:
            return 'Otros';
        }
      })(),
    };
  } catch (error) {
    console.error('Error fetching location fields:', error);
    return undefined;
  }
}
function getRegionByCountryCode(countryCode: string): 'LATAM' | 'EU' | 'OTHER' {
  const countryToRegionMap: { [string]: 'LATAM' | 'EU' | 'OTHER' } = {
    // ─── LATAM ─────────────────────────────────────────
    MX: 'LATAM',
    GT: 'LATAM',
    HN: 'LATAM',
    SV: 'LATAM',
    NI: 'LATAM',
    CR: 'LATAM',
    PA: 'LATAM',
    BZ: 'LATAM',
    CU: 'LATAM',
    DO: 'LATAM',
    HT: 'LATAM',
    JM: 'LATAM',
    PR: 'LATAM',
    TT: 'LATAM',
    BS: 'LATAM',
    BB: 'LATAM',
    GD: 'LATAM',
    LC: 'LATAM',
    VC: 'LATAM',
    AG: 'LATAM',
    DM: 'LATAM',
    KN: 'LATAM',
    AR: 'LATAM',
    BO: 'LATAM',
    BR: 'LATAM',
    CL: 'LATAM',
    CO: 'LATAM',
    EC: 'LATAM',
    GY: 'LATAM',
    PY: 'LATAM',
    PE: 'LATAM',
    SR: 'LATAM',
    UY: 'LATAM',
    VE: 'LATAM',

    // ─── EUROPE ────────────────────────────────────────
    AL: 'EU',
    AD: 'EU',
    AT: 'EU',
    BY: 'EU',
    BE: 'EU',
    BA: 'EU',
    BG: 'EU',
    HR: 'EU',
    CY: 'EU',
    CZ: 'EU',
    DK: 'EU',
    EE: 'EU',
    FI: 'EU',
    FR: 'EU',
    DE: 'EU',
    GR: 'EU',
    HU: 'EU',
    IS: 'EU',
    IE: 'EU',
    IT: 'EU',
    LV: 'EU',
    LI: 'EU',
    LT: 'EU',
    LU: 'EU',
    MT: 'EU',
    MD: 'EU',
    MC: 'EU',
    ME: 'EU',
    NL: 'EU',
    MK: 'EU',
    NO: 'EU',
    PL: 'EU',
    PT: 'EU',
    RO: 'EU',
    RU: 'EU',
    SM: 'EU',
    RS: 'EU',
    SK: 'EU',
    SI: 'EU',
    ES: 'EU',
    SE: 'EU',
    CH: 'EU',
    UA: 'EU',
    GB: 'EU',
    VA: 'EU',
  };

  return countryToRegionMap[countryCode] ?? 'OTHER';
}

function getSearchEngines() {
  return ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.'];
}

function getSocialNetworks() {
  return [
    'facebook.',
    'instagram.',
    'twitter.',
    't.co',
    'x.com',
    'linkedin.',
    'lnkd.in',
    'pinterest.',
    'reddit.',
    'tiktok.',
    'snapchat.',
  ];
}
